// src/services/supportApi.ts
//
// WS-D — Tiny client for /api/support/* (Netlify function `support.ts`).
// Same auth pattern as adminApi: Bearer JWT + x-tenant-slug header.

import axios, { type AxiosInstance } from 'axios';

const BASE = '/api/support';

export interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;            // 'open' | 'in-progress' | 'resolved' | 'closed'
  priority: string;          // 'low' | 'medium' | 'high' | 'urgent'
  category: string;
  customer_name: string | null;
  customer_email: string | null;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface TicketResponse {
  id: string;
  ticket_id: string;
  author: string;
  author_type: 'customer' | 'support';
  message: string;
  attachments: unknown[];
  timestamp: string;
}

interface Envelope<T> { success: boolean; data?: T; error?: string; notLinked?: boolean; message?: string; }

export interface ListTicketsResult {
  tickets: Ticket[];
  notLinked: boolean;
  message: string | null;
}

function buildClient(token?: string | null, slug?: string | null): AxiosInstance {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (slug) headers['x-tenant-slug'] = slug;
  return axios.create({ baseURL: '', timeout: 30000, headers });
}

class SupportApiService {
  private _client: AxiosInstance;
  private _token: string | null = null;
  private _slug: string | null = null;

  constructor() { this._client = buildClient(); }

  setAuthToken(token: string | null)  { this._token = token; this._client = buildClient(this._token, this._slug); }
  setTenantSlug(slug: string | null)  { this._slug  = slug;  this._client = buildClient(this._token, this._slug); }

  async listTickets(status: string = 'active'): Promise<ListTicketsResult> {
    const r = await this._client.get<Envelope<Ticket[]>>(`${BASE}/tickets?status=${encodeURIComponent(status)}`);
    if (!r.data.success) throw new Error(r.data.error || 'Support API request failed');
    return {
      tickets: r.data.data ?? [],
      notLinked: !!r.data.notLinked,
      message: r.data.message ?? null,
    };
  }

  async getTicket(id: string): Promise<{ ticket: Ticket; responses: TicketResponse[] }> {
    const r = await this._client.get<Envelope<{ ticket: Ticket; responses: TicketResponse[] }>>(
      `${BASE}/tickets/${encodeURIComponent(id)}`,
    );
    return this.unwrap(r.data);
  }

  async createTicket(body: { subject: string; description: string; priority?: string; category?: string }): Promise<Ticket> {
    const r = await this._client.post<Envelope<Ticket>>(`${BASE}/tickets`, body);
    return this.unwrap(r.data);
  }

  async addResponse(ticketId: string, message: string): Promise<TicketResponse> {
    const r = await this._client.post<Envelope<TicketResponse>>(
      `${BASE}/tickets/${encodeURIComponent(ticketId)}/responses`,
      { message },
    );
    return this.unwrap(r.data);
  }

  private unwrap<T>(env: Envelope<T>): T {
    if (!env.success) throw new Error(env.error || 'Support API request failed');
    return env.data as T;
  }
}

export const supportApi = new SupportApiService();
